import { Injectable, Predicate } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Contact,
} from '@omni/classes/contact/contact.class';
import { ContactDataService } from '@omni/data-services/contact/contact.data.service';
import {
  CustomCategorySearchToolName,
  SelectedSuggestionPillDataModel,
  SuggestionPillType,
} from '@omni/models/search-config-data-model';
import { ActivityService } from '../activity/activity.service';
import { ConsentService } from '../consent/consent.service';
import { ContactOfflineService } from '../contact/contact.service';
import { CustomerAssessService } from '../customer-assess/customer-assess.service';
import { EventsToolService } from '../events-tool/events-tool.service';
import { ScientificActivityService } from '../scientific-activity/scientific-activity.service';
import { SearchConfigService } from '../search/search-config.service';
import Fuse from 'fuse.js';
import { ActivityType } from '@omni/classes/activity/activity.class';
import { GlobalUtilityService } from '../global-utility.service';
import { CONTACT_CONTACT_AFFILIATIONS_REF_ENTITY } from '@omni/config/dynamic-forms/affiliations-contants';
import {
  AssessmentAttributeType,
  EntityOptions,
} from '@omni/classes/customer-assessment/assessment-template.class';
import { AuthenticationService } from '../authentication.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { DiskService } from '../disk/disk.service';
import { ControlDataType } from '@omni/classes/dynamic-form/dynamic-form.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { PhoneActivity } from '@omni/classes/activity/phone.activity.class';
import { EmailActivity } from '@omni/classes/activity/email.activity.class';
import { SampleActivity } from '@omni/classes/activity/sample.activity.class';
import { CaseActivity } from '@omni/classes/case-intake/case-activity.class';
import { SampleService } from '../sample/sample.service';
import { DeviceService } from '../device/device.service';
import { SearchConfigDataService } from '@omni/data-services/search-config/search-config-data-service';
import { cloneDeep, get, isEmpty, pick } from 'lodash-es';
import type { Collection } from 'lodash';
import { _ } from '@omni/utility/lodash-chain';

type FilterConfig = SelectedSuggestionPillDataModel;
type SearchConfig =
  (typeof SearchConfigService.prototype.contactConfiguredSearchIndexConfig)[number];

type DateFilterConfig = {
  startDate: Date;
  endDate: Date;
};

@Injectable({
  providedIn: 'root',
})
export class AdvancedCustomerSearchService {
  constructor(
    private readonly translate: TranslateService,
    private readonly contactService: ContactOfflineService,
    private readonly searchConfigService: SearchConfigService,
    private readonly scientificActivityService: ScientificActivityService,
    private readonly consentService: ConsentService,
    private readonly activityService: ActivityService,
    private readonly eventsToolService: EventsToolService,
    private readonly customerAssessService: CustomerAssessService,
    private readonly utilityService: GlobalUtilityService,
    private readonly authenticationOfflineService: AuthenticationService,
    private readonly contactDataService: ContactDataService,
    private readonly disk: DiskService,
    private readonly sampleService: SampleService,
    private readonly device: DeviceService,
    private readonly searchConfigDataService: SearchConfigDataService,
    private readonly sapService: ScientificActivityService,
  ) {}

  private matchLocalized(item: string, value: string): boolean;
  private matchLocalized<X, Y>(
    selector: (item: X) => Y,
    value: string,
  ): (item: X) => boolean;
  private matchLocalized<X, Y>(
    selector: ((item: X) => Y) | string,
    value: string,
  ): ((item: X) => boolean) | boolean {
    if (typeof selector === 'string') {
      return value === selector || this.translate.instant(value) === selector;
    }
    return (item: X) => {
      const itemValue = selector(item);
      return itemValue === value || this.translate.instant(value) === itemValue;
    };
  }

  private mapForUserTag(
    selectedData: SelectedSuggestionPillDataModel[],
  ): SelectedSuggestionPillDataModel[] {
    const isTag = this.matchLocalized(
      (x: SelectedSuggestionPillDataModel) => x.categoryName,
      'Tag',
    );
    const tagFilters = selectedData.filter(isTag);
    const nonTagFilters = selectedData.filter((item) => !isTag(item));

    // Collect unique contact IDs from tags
    const contactIdsFromTags = tagFilters
      .flatMap(
        (tag) =>
          this.contactService.contactTags.find(
            (t) => t.indskr_name === tag.selectedFacet,
          )?.contacts ?? [],
      )
      .map((c) => c.contactId);

    const uniqueContactIds = [...new Set(contactIdsFromTags)];

    const syntheticFilters: SelectedSuggestionPillDataModel[] =
      uniqueContactIds.map((id) => ({
        selectedFacet: id,
        categoryPath: 'accountid',
        categoryName: 'Tag',
        categoryDisplayName: 'Tag',
        isSubCategory: false,
      }));

    return [...syntheticFilters, ...nonTagFilters];
  }

  //Character search only on Name of the contact
  private getOjectValuesEntityLevel(conObject: any): string {
    const value = get(conObject, 'fullname');
    const results = Array.isArray(value) ? value.map((item: any) => this.getOjectValuesEntityLevel(item)) : [value];
    const combined = results.filter(Boolean).join(',').toUpperCase();
    return this.searchConfigService.convertFormattedString(combined);
  }
  private getEntityLevelCharSearchPredicate(
    filters: FilterConfig[],
  ): Predicate<Contact> {
    const charSearchFilter = filters.find(
      (f) => f.type === SuggestionPillType.ENTITY_LEVEL_CHARACTERSEARCH,
    );

    const searchTerms = charSearchFilter?.charSearchText
      ? this.searchConfigService
          .convertFormattedString(charSearchFilter.charSearchText)
          .toUpperCase()
          .split(' ')
      : [];

    if (searchTerms.length === 0) return;

    return (contact: Contact) => {
      const contactData = this.getOjectValuesEntityLevel(contact);
      return searchTerms.some((term) => contactData.includes(term));
    };
  }

  private getListTagPredicate(filters: FilterConfig[]): Predicate<Contact> {
    const tagFilter = filters.find((f) => f.categoryName === 'listTag');
    if (!tagFilter) return;

    return (contact: Contact) =>
      contact.tagLabels?.includes(tagFilter.selectedFacet);
  }

  private populateConfiguredFrom(filters: FilterConfig[]) {
    for (const filter of filters) {
      if (!filter.configuredFrom && filter.categoryName) {
        const match =
          this.searchConfigService.contactConfiguredSearchIndexConfig.find(
            (cfg) => cfg.categoryName === filter.categoryName,
          );
        if (match?.configuredFrom) {
          filter.configuredFrom = match.configuredFrom;
        }
      }
    }
  }

  private groupFiltersByCategory(
    filters: FilterConfig[],
  ): Record<string, FilterConfig[]> {
    return _(filters)
      .filter(
        (f) =>
          f.categoryName &&
          (!f.isSubCategory || f.configuredFrom === 'Assess') &&
          !['listTag', 'tag'].includes(f.categoryName),
      )
      .groupBy('categoryName')
      .value();
  }

  private async getGroupedPredicates(
    query: Collection<Contact>,
    filterGroups: Record<string, FilterConfig[]>,
    dateFilter: DateFilterConfig,
  ): Promise<Predicate<Contact>[]> {
    let predicates: Predicate<Contact>[] = [];
    for (const [category, filters] of Object.entries(filterGroups)) {
      let orPredicates: Predicate<Contact>[] = [];
      if (this.matchLocalized(category, 'Assessments')) {
        orPredicates.push(this.getAssessmentsSubPredicate(filters));
      }

      for (const filter of filters) {
        orPredicates.push(
          await this.getControlDataTypePredicate(filter, query, dateFilter),
        );
      }
      orPredicates = orPredicates.filter(Boolean);
      if (!orPredicates.length) {
        continue;
      }
      predicates.push((contact: Contact) =>
        orPredicates.some((predicate) => predicate(contact)),
      );
    }
    return predicates;
  }

  private getAssessmentsSubPredicate(
    filters: FilterConfig[],
  ): Predicate<Contact> {
    const hasAssess = filters.some(
      this.matchLocalized((x) => x.selectedFacet, 'Assessed'),
    );
    const hasUnassess = filters.some(
      this.matchLocalized((x) => x.selectedFacet, 'UnAssessed'),
    );

    if (!hasAssess || !hasUnassess) {
      const contactIds = _(this.customerAssessService.assessedContacts)
        .map((c) => c.contactid)
        .uniq()
        .value();

      return (contact: Contact) =>
        contactIds.includes(contact.ID) === hasAssess;
    }
  }

  private doesSearchConfigMatch(
    searchConfig: SearchConfig,
    filter: FilterConfig,
  ): boolean {
    if (filter.configuredFrom === 'Assess') {
      return searchConfig.categoryName === filter.categoryName || searchConfig.categoryName === filter.categoryPath;
    } else if (filter.categoryPath) {
      return searchConfig.categoryRelativePath === filter.categoryPath;
    }
    return searchConfig.categoryName === filter.categoryName;
  }

  private applySpecialConfigAdjustments(
    filter: FilterConfig,
    searchConfig: SearchConfig,
  ): void {
    if (searchConfig) {
      filter.categoryPath = searchConfig.categoryRelativePath;
    }

    if (filter.categoryPath === 'indskr_primaryaccount') {
      const adjustedPath = 'indskr_accountcontactaffiliation.indskr_accountid';
      filter.categoryPath = adjustedPath;
      Object.assign(searchConfig, {
        categoryRelativePath: adjustedPath,
        controlDataType: 'LinkedEntity',
        relationshipName: 'indskr_contact_accountcontactaffiliation_contact',
        parentEntity: 'indskr_accountcontactaffiliation',
      });
    }
  }

  private async getControlDataTypePredicate(
    filter: FilterConfig,
    query: Collection<Contact>,
    dateFilter: DateFilterConfig,
  ): Promise<Predicate<Contact>> {
    const searchConfig =
      this.searchConfigService.contactConfiguredSearchIndexConfig.find((c) =>
        this.doesSearchConfigMatch(c, filter),
      );

    if (!searchConfig) return;

    // Set additional props on config if needed
    this.applySpecialConfigAdjustments(filter, searchConfig);

    // Route by controlDataType
    switch (searchConfig.controlDataType) {
      case 'DefaultConfig':
        return await this.getDefaultConfigPredicate(filter, query, dateFilter);

      case 'LinkedEntity':
        return await this.getLinkedEntityPredicate(filter, searchConfig);

      case 'Assess':
        return await this.getAssessPredicate(filter, query, searchConfig);

      default:
        return this.getRawFieldPredicate(filter, searchConfig);
    }
  }

  private fuseSearch<T>(
    items: T[],
    filter: FilterConfig,
    threshold = 0.1,
    keys = [filter.categoryPath],
    searchText = filter.selectedFacet,
  ) {
    const base = {
      keys,
      threshold: 0,
      distance: 100,
      includeScore: true,
    };
    if (
      filter.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH ||
      filter.type === SuggestionPillType.BOOLEAN_FIELD
    ) {
      base.threshold = threshold;
      base.distance = 1000;
      searchText = filter.charSearchText;
    }
    return _(new Fuse(items, base).search(searchText ?? ''))
      .filter(
        (r: any) => r.score < 0.1 ||
          filter.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH,
      )
      .map((r: any) => r.item);
  }

  private getConsentPredicate(
    query: Collection<Contact>,
    filter: FilterConfig,
  ): Predicate<Contact> {
    const activeConsents =
      this.consentService.allActiveConsentSubject.value.filter((consent) =>
        query.some((c) => c.ID === consent.indskr_contactid),
      );

    const result = this.fuseSearch(activeConsents, filter);
    const contactIds = result.map('indskr_contactid').uniq().value();

    return (contact: Contact) => contactIds.includes(contact.ID);
  }

  private getScientificPlansPredicate(
    query: Collection<Contact>,
    filter: FilterConfig,
  ): Predicate<Contact> {
    const sciPlans = this.scientificActivityService.sapActivities.filter(
      (sapAct) =>
        sapAct.contacts.some((c) => query.some((fc) => fc.ID === c.ID)),
    );

    const result = this.fuseSearch(sciPlans, filter);
    const contactIds = result.flatMap('contacts').map('ID').uniq().value();

    return (contact: Contact) => contactIds.includes(contact.ID);
  }

  private getMyActivitiesPredicate(
    filter: FilterConfig,
    dateFilter: DateFilterConfig,
  ): Predicate<Contact> {
    const allActivities = this.getMergedActivityList();
    const filteredActivities = this.filterActivitiesByDate(
      allActivities,
      dateFilter,
    );
    const selectedSubCategories = filter.subCategories ?? [];

    const contactIds = this.extractActivityContactIds(
      filter.selectedFacet,
      selectedSubCategories,
      filteredActivities,
    );

    return (contact: Contact) => contactIds.includes(contact.ID);
  }

  private getMergedActivityList(): any[] {
    const regular = cloneDeep(this.activityService.activities);
    const events = cloneDeep(this.eventsToolService.eventsToolData || []);

    for (const e of events) {
      e.scheduledStart = new Date(e._startDate);
    }

    return [...regular, ...events];
  }

  private filterActivitiesByDate(
    activities: any[],
    dateFilter: DateFilterConfig,
  ): any[] {
    if (!dateFilter?.startDate || !dateFilter?.endDate) return activities;
    const { startDate, endDate } = dateFilter;

    return activities.filter((activity) => {
      const date = new Date(activity.scheduledStart);
      return date >= startDate && date <= endDate;
    });
  }

  private extractActivityContactIds(
    selectedFacet: string,
    subCategories: any[],
    activities: any[],
  ): string[] {
    const t = this.translate.instant.bind(this.translate);
    const contactIds: string[] = [];

    const statusFilters = {
      meetings: {
        open: this.hasSubCategoryChecked(
          subCategories,
          'filterMeetingsStatus',
          t('OPEN'),
        ),
        completed: this.hasSubCategoryChecked(
          subCategories,
          'filterMeetingsStatus',
          t('COMPLETED'),
        ),
      },
      messages: {
        delivered: this.hasSubCategoryChecked(
          subCategories,
          'filterMessagesStatus',
          t('DELIVERED'),
        ),
        read: this.hasSubCategoryChecked(
          subCategories,
          'filterMessagesStatus',
          t('READ'),
        ),
        attachYes: this.hasSubCategoryChecked(
          subCategories,
          'filterMessagesAttachment',
          t('YES'),
        ),
        attachNo: this.hasSubCategoryChecked(
          subCategories,
          'filterMessagesAttachment',
          t('NO'),
        ),
        linkYes: this.hasSubCategoryChecked(
          subCategories,
          'filterMessagesLinkAccessed',
          t('YES'),
        ),
        linkNo: this.hasSubCategoryChecked(
          subCategories,
          'filterMessagesLinkAccessed',
          t('NO'),
        ),
      },
    };

    const match = (label: string, ...args: any[]) =>
      selectedFacet === t(label, ...args);

    if (match('MEETINGS')) {
      const codes = [];
      if (statusFilters.meetings.completed) codes.push(1);
      if (statusFilters.meetings.open) codes.push(0);

      contactIds.push(
        ...activities
          .filter(
            (a) =>
              a.type === ActivityType.Appointment && a.location !== 'LiveMeet',
          )
          .filter((a) => !codes.length || codes.includes(a.state))
          .flatMap((a) => a.contacts ?? [])
          .map((c) => c.ID),
      );
    }

    if (match('LIVE_MEETING')) {
      contactIds.push(
        ...activities
          .filter(
            (a) =>
              a.type === ActivityType.Appointment && a.location === 'LiveMeet',
          )
          .flatMap((a) => a.contacts ?? [])
          .map((c) => c.ID),
      );
    }

    if (match('PHONE_CALLS')) {
      contactIds.push(
        ...activities
          .filter(
            (a) =>
              a.type === ActivityType.PhoneCall &&
              (a.state === 1 || a.statusString === 'Completed'),
          )
          .flatMap((a) => a.raw_contactAttendees ?? [])
          .map((c) => c.indskr_contactid),
      );
    }

    if (match('MESSAGES')) {
      let result = activities.filter(
        (a) =>
          a.type === ActivityType.Email &&
          (a.emailStatus === 3 || a.emailStatus === 548910000),
      );

      if (statusFilters.messages.read) {
        result = result.filter((a) =>
          a.emailActivityParties?.some((p) =>
            [0, 4].includes(p.email_statuscode),
          ),
        );
      }
      if (statusFilters.messages.delivered) {
        result = result.filter((a) =>
          a.emailActivityParties?.some((p) =>
            [3, 548910000].includes(p.email_statuscode),
          ),
        );
      }
      if (statusFilters.messages.linkNo) {
        result = result.filter(
          (a) =>
            _(a.emailActivityParties)
              .flatMap((p) => p.emailAddresses)
              .map(
                (e) =>
                  (e.email_linksclickedcount ?? 0) +
                  (e.email_attachmentopencount ?? 0),
              )
              .sum() === 0,
        );
      }
      if (statusFilters.messages.linkYes) {
        result = result.filter(
          (a) =>
            _(a.emailActivityParties)
              .flatMap((p) => p.emailAddresses)
              .map(
                (e) =>
                  (e.email_linksclickedcount ?? 0) +
                  (e.email_attachmentopencount ?? 0),
              )
              .sum() > 0,
        );
      }
      if (
        statusFilters.messages.attachYes &&
        !statusFilters.messages.attachNo
      ) {
        result = result.filter((a) => !isEmpty(a.emailAttachments));
      } else if (statusFilters.messages.attachNo) {
        result = result.filter((a) => isEmpty(a.emailAttachments));
      }

      contactIds.push(
        ...result
          .flatMap((a) => a.emailActivityParties)
          .map((p) => p.indskr_contactid),
      );
    }

    if (match('ALLOCATION_ORDER')) {
      contactIds.push(
        ...activities
          .filter(
            (a) =>
              a.type === ActivityType.Sample &&
              (a.state === 1 || a.statusString === 'Completed'),
          )
          .map((a) => a.contactID),
      );
    }

    if (
      match('CUSTOMER_INQUIRIES', {
        globalCustomerText: this.utilityService.globalCustomerText,
      })
    ) {
      contactIds.push(
        ...activities
          .filter((a) => a.type === ActivityType.CaseIntake)
          .map((a) => a.raw['customerid_value']),
      );
    }

    if (match('EVENTS')) {
      contactIds.push(
        ...activities
          .filter((a) => a.type === ActivityType.Event)
          .flatMap((a) => a.participants)
          .map((p) => p.id),
      );
    }

    return contactIds.filter(Boolean);
  }

  private hasSubCategoryChecked(
    subCats: any[],
    id: string,
    label: string,
  ): boolean {
    return subCats.some(
      (cat) =>
        cat.id === id &&
        cat.childItems?.some((c) => c.isChecked && c.label === label),
    );
  }

  private async getLinkedEntityPredicate(
    filter: FilterConfig,
    searchConfig: SearchConfig,
  ): Promise<Predicate<Contact>> {
    const dbKey =
      searchConfig.parentEntity !== CONTACT_CONTACT_AFFILIATIONS_REF_ENTITY
        ? searchConfig.parentEntity
        : searchConfig.relationshipName;

    const rawLEData = await this.disk.retrieve(dbKey);
    if (!rawLEData?.raw) return;

    const linkedEntityData = rawLEData.raw;
    const suffix = this.getLinkedEntitySuffix(searchConfig, filter);
    const searchText = this.getLinkedEntitySearchText(filter, searchConfig);
    const filteredLinkedData = this.filterLinkedEntityData(
      linkedEntityData,
      filter,
      searchText,
      suffix,
    );
    const contactIds = _(filteredLinkedData)
      .map((entry) => entry.contactid)
      .uniq()
      .value();

    return (contact: Contact) => contactIds.includes(contact.ID);
  }

  private getLinkedEntitySuffix(
    searchConfig: SearchConfig,
    filter: FilterConfig,
  ): string {
    if (
      searchConfig.isMultilingualLookup &&
      filter.type === SuggestionPillType.CATEGORY_VALUESEARCH
    ) {
      return '';
    }
    return '@OData.Community.Display.V1.FormattedValue';
  }

  private getLinkedEntitySearchText(
    filter: FilterConfig,
    searchConfig: SearchConfig,
  ): string {
    if (filter.type === SuggestionPillType.CATEGORY_VALUESEARCH && searchConfig.isMultilingualLookup) {
      const mappedValue = searchConfig.mappingValues.find(
        (m) => m.formattedValue === filter.selectedFacet,
      )?.actualValue;
      if (mappedValue) {
        return mappedValue;
      }
    }
    if (filter.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH) {
      return filter.charSearchText;
    }
    return filter.selectedFacet;
  }

  private filterLinkedEntityData(
    data: any[],
    filter: FilterConfig,
    searchText: string,
    suffix: string,
  ): any[] {
    const field = filter.categoryPath;
    const fields = [field + suffix, field];

    if (filter.type === SuggestionPillType.CATEGORY_VALUESEARCH) {
      return data.filter((item) => {
        for (const field of fields) {
          const val = item[field];
          if (val === searchText) {
            return true;
          }
        }
        return false;
      });
    }

    if (filter.type === SuggestionPillType.CATEGORY_CHARACTERSEARCH) {
      const formattedSearchText = this.searchConfigService
        .convertFormattedString(searchText)
        .toLowerCase();

      return data.filter((item) => {
        for (const field of fields) {
          const val = item[field];
          const convertedVal = this.searchConfigService.convertFormattedString(val).toLowerCase() || '';
          if (convertedVal.includes(formattedSearchText)) {
            return true;
          }
        }
        return false;
      });
    }

    return [];
  }

  private async getAssessPredicate(
    filter: FilterConfig,
    query: Collection<Contact>,
    searchConfig: SearchConfig,
  ): Promise<Predicate<Contact>> {
    const isChoice = searchConfig.categoryRelativePath === 'indskr_choicelabel';
    const searchType = isChoice
      ? AssessmentAttributeType.CHOICE
      : AssessmentAttributeType.FLAG;

    const categoryName =
      searchConfig.categoryNameInEng || searchConfig.categoryName;

    const searchText =
      isChoice && filter.selectedFacetInEng
        ? filter.selectedFacetInEng
        : filter.charSearchText;

    const assessments =
      await this.customerAssessService.getAssessmentResponsesForOnlineSearch(
        EntityOptions.CONTACT,
        searchType,
        categoryName,
      );

    const latestByCustomer = _(assessments)
      .filter((a) => query.some((c) => c.ID === a['ca.indskr_customer']))
      .uniqBy('ca.indskr_customer')
      .orderBy(['modifiedon', 'createdon'], ['desc', 'desc'])
      .value();

    if (isChoice && filter.type === SuggestionPillType.CATEGORY_VALUESEARCH) {
      const matched = latestByCustomer.filter((a) =>
        a.indskr_choicelabel?.split(',')?.includes(searchText),
      );

      return contact => matched.some((a) => a['ca.indskr_customer'] === contact.ID);
    }

    const contactIds = this.fuseSearch(
      latestByCustomer,
      filter,
      0.2,
      ['indskr_choicelabel'],
      searchText,
    )
      .map('ca.indskr_customer')
      .uniq()
      .value();

    return (contact: Contact) => contactIds.includes(contact.ID);
  }

  private isBooleanField(filter: FilterConfig): boolean {
    return filter.type === SuggestionPillType.BOOLEAN_FIELD;
  }

  private async loadTargetContacts(positionId: string): Promise<void> {
    const hasCLM = this.authenticationOfflineService.hasFeatureAction(
      FeatureActionsMap.CUSTOMER_LIST_MANAGEMENT,
    );

    if (hasCLM && !this.searchConfigService.isTargetContactsPillRemoved) {
      let response = await this.contactDataService
        .getTargetContactsBasedOnPositionId(positionId)
        .catch(() => []);

      if (response.length > 0) {
        if (response[0].customerListPeriodName) {
          this.searchConfigService.isTargetContactsPillRemoved = true;
        }
      } else {
        const fallback = await this.contactDataService
          .getContactsBasedOnPositionId(positionId)
          .catch(() => []);
        if (fallback.length > 0) {
          this.contactService.globalSearchResults = [];
        }
      }
    } else {
      const fallback = await this.contactDataService
        .getContactsBasedOnPositionId(positionId)
        .catch(() => []);
      if (fallback.length > 0) {
        this.contactService.globalSearchResults = [];
      }
    }
  }

  private async getDefaultConfigPredicate(
    filter: FilterConfig,
    query: Collection<Contact>,
    dateFilter: DateFilterConfig,
  ): Promise<Predicate<Contact>> {

    if (
      filter.categoryPath?.includes('activeConsents') &&
      !this.isBooleanField(filter)
    ) {
      return this.getConsentPredicate(query, filter);
    }

    if (filter.categoryName === 'Scientific Plans') {
      return this.getScientificPlansPredicate(query, filter);
    }

    if (filter.categoryPath === 'myActivitiesByContact') {
      return this.getMyActivitiesPredicate(filter, dateFilter);
    }

    if (
      filter.categoryPath === 'hasTeamusers' &&
      filter.selectedFacet &&
      filter.value
    ) {
      await this.loadTargetContacts(filter.value);
      return; // data is globally updated
    }

    // Default Fuse search for this category
    return this.getFusePredicate(query, filter);
  }

  private getFusePredicate(
    query: Collection<Contact>,
    filter: FilterConfig,
  ): Predicate<Contact> {
    let threshold: number;
    if (filter.categoryPath === 'allPhoneNumbers') {
      threshold = 0.05;
    }

    const results = this.fuseSearch(query.value(), filter, threshold);

    return (contact: Contact) => results.some((r) => r.ID === contact.ID);
  }

  private getTagSuggestionPredicate(
    filters: FilterConfig[],
  ): Predicate<Contact> {
    const tagFilters = filters.filter((f) =>
      ['Tag', 'tag', this.translate.instant('TAG')].includes(f.categoryName),
    );

    if (!tagFilters.length) return;

    return (contact: Contact) =>
      tagFilters.some((f) => f.selectedFacet === contact.ID);
  }

  private getRawFieldPredicate(
    filter: FilterConfig,
    searchConfig: SearchConfig,
  ): Predicate<Contact> {
    let searchKey = this.getRawFieldKey(filter, searchConfig);
    let searchText = this.getRawSearchText(filter, searchConfig);

    switch (filter.type) {
      case SuggestionPillType.CATEGORY_VALUESEARCH:
      case SuggestionPillType.BOOLEAN_FIELD:
        return (contact: Contact) => contact.raw[searchKey] === searchText;

      case SuggestionPillType.CATEGORY_CHARACTERSEARCH:
        return (contact: Contact) => {
          const val = contact.raw[searchKey];
          const contactVal = val
            ? this.searchConfigService.convertFormattedString(val).toLowerCase()
            : '';
          const target = this.searchConfigService
            .convertFormattedString(searchText)
            .toLowerCase();
          return contactVal.includes(target);
        };

      case SuggestionPillType.CUSTOM_CATEGORY_CHARACTERSEARCH:
        searchKey = 'fullname';
        return (contact: Contact) => {
          const val = contact.raw[searchKey];
          const contactVal = val
            ? this.searchConfigService.convertFormattedString(val).toLowerCase()
            : '';
          const target = this.searchConfigService
            .convertFormattedString(filter.charSearchText)
            .toLowerCase();
          return (
            contact.raw.hasOwnProperty('contactid') &&
            contactVal.includes(target)
          );
        };

      case SuggestionPillType.CUSTOM_CATEGORY_VALUESEARCH:
        searchKey = 'contactid';
        return (contact: Contact) => contact.raw[searchKey] === filter.id;

      default:
        return; // no-op if unknown type
    }
  }

  private getRawFieldKey(
    filter: FilterConfig,
    searchConfig: SearchConfig,
  ): string {
    let suffix = '';
    let prefix = '';

    const type = searchConfig.controlDataType;

    if (
      [
        ControlDataType.PicklistType,
        ControlDataType.BooleanType,
        ControlDataType.MultiSelectPicklistType,
        ControlDataType.StatusType,
        ControlDataType.StateType,
      ].includes(type as ControlDataType)
    ) {
      suffix = '@OData.Community.Display.V1.FormattedValue';
    } else if (type === ControlDataType.LookupType) {
      if (
        searchConfig.isMultilingualLookup &&
        filter.type === SuggestionPillType.CATEGORY_VALUESEARCH
      ) {
        suffix = '_value';
        prefix = '_';
      } else {
        suffix = '_value@OData.Community.Display.V1.FormattedValue';
        prefix = '_';
      }
    }

    return prefix + searchConfig.categoryRelativePath + suffix;
  }

  private getRawSearchText(
    filter: FilterConfig,
    searchConfig: SearchConfig,
  ): string {
    if (
      searchConfig.isMultilingualLookup &&
      filter.type === SuggestionPillType.CATEGORY_VALUESEARCH
    ) {
      return (
        searchConfig.mappingValues.find(
          (m) => m.formattedValue === filter.selectedFacet,
        )?.actualValue ?? filter.selectedFacet
      );
    }

    return filter.selectedFacet || filter.charSearchText || '';
  }

  private getContacts(): Contact[] {
    return this.contactService.contacts;
  }

  private initializeSubFilterSelections(filters: FilterConfig[]): void {
    this.contactService.selectedActivitiesFromContactFilter = [];
    this.contactService.selectedSubOptionsFromContactFilter = {
      meetingStatus: [],
    };

    for (const data of filters) {
      if (data.categoryPath === 'myActivitiesByContact') {
        const value = this.contactService.convertOptionValueToActivityType(
          data.selectedFacet,
        );
        this.contactService.selectedActivitiesFromContactFilter.push(value);
      } else if (data.categoryPath === 'filterMeetingsStatus') {
        const val = data.selectedFacet;
        if (val === this.translate.instant('COMPLETED')) {
          this.contactService.selectedSubOptionsFromContactFilter.meetingStatus.push(
            3,
          );
        } else if (val === this.translate.instant('OPEN')) {
          this.contactService.selectedSubOptionsFromContactFilter.meetingStatus.push(
            1,
          );
        }
      }
    }
  }

  private mapActivitiesToContactsSearchConfig() {
    //reset the selected Activities which is to track the selected Activities for use in the Timeline filter
    this.contactService.selectedActivitiesFromContactFilter = [];
    this.contactService.selectedSubOptionsFromContactFilter = {
      meetingStatus: [],
    };
    const activities = _(this.activityService.activities);
    const myActivitiesByContact =
      this.searchConfigService.contactConfiguredSearchIndexConfig.find(
        (config) => config.categoryRelativePath == 'myActivitiesByContact',
      );
    myActivitiesByContact.values = [];

    const hasAppointmentContacts = activities
      .filter(
        (activity) =>
          activity instanceof AppointmentActivity &&
          activity.type == ActivityType.Appointment &&
          activity.location != 'LiveMeet',
      )
      .flatMap((activity) => (activity as AppointmentActivity).contacts)
      .some();
    const meetingsText = this.translate.instant('MEETINGS');
    if (
      hasAppointmentContacts &&
      !myActivitiesByContact.values.includes(meetingsText)
    ) {
      myActivitiesByContact.values.push(meetingsText);
      let filterMeetingsStatus =
        this.searchConfigService.contactConfiguredSearchIndexConfig.find(
          (config) => config.categoryRelativePath == 'filterMeetingsStatus',
        );
      if (!filterMeetingsStatus) {
        filterMeetingsStatus = {
          categoryName: 'Meetings_Status',
          translationKey: 'FILTER_MEETINGS_STATUS',
          categoryRelativePath: 'filterMeetingsStatus',
          controlDataType: 'DefaultConfig',
          values: [],
          mappingValues: [],
          isFixedFilter: true,
          filterOrder: '2',
          isForcedHide: true,
          isSubCategory: true,
        };
        this.searchConfigService.contactConfiguredSearchIndexConfig.push(
          filterMeetingsStatus,
        );
      }
      filterMeetingsStatus.values = [
        this.translate.instant('COMPLETED'),
        this.translate.instant('OPEN'),
      ];
    }

    const hasLiveMeetingActivities = activities
      .filter(
        (activity) =>
          activity instanceof AppointmentActivity &&
          activity.type == ActivityType.Appointment &&
          activity.location == 'LiveMeet',
      )
      .flatMap((activity) => (activity as AppointmentActivity).contacts)
      .some();
    const liveMeetingsText = this.translate.instant('LIVE_MEETING');
    if (
      hasLiveMeetingActivities &&
      !myActivitiesByContact.values.includes(liveMeetingsText)
    ) {
      myActivitiesByContact.values.push(liveMeetingsText);
    }

    const hasPhoneCallActivities = activities
      .filter(
        (activity) =>
          activity instanceof PhoneActivity &&
          activity.type == ActivityType.PhoneCall &&
          (activity.state == 1 || activity.statusString == 'Completed'),
      )
      .flatMap((activity) => (activity as PhoneActivity).raw_contactAttendees)
      .some();

    const phoneCallsText = this.translate.instant('PHONE_CALLS');
    if (
      hasPhoneCallActivities &&
      !myActivitiesByContact.values.includes(phoneCallsText)
    ) {
      myActivitiesByContact.values.push(phoneCallsText);
    }

    const hasMessages = activities
      .filter(
        (activity) =>
          activity instanceof EmailActivity &&
          activity.type == ActivityType.Email &&
          (activity.emailStatus == 3 || activity.emailStatus == 548910000),
      )
      .flatMap((activity) => (activity as EmailActivity).emailActivityParties)
      .some((party) => !!party.indskr_contactid);
    const messagesText = this.translate.instant('MESSAGES');
    if (
      hasMessages &&
      !myActivitiesByContact.values.includes(messagesText)
    ) {
      myActivitiesByContact.values.push(messagesText);
      let filterMessagesStatus =
        this.searchConfigService.contactConfiguredSearchIndexConfig.find(
          (config) => config.categoryRelativePath == 'filterMessagesStatus',
        );
      if (!filterMessagesStatus) {
        filterMessagesStatus = {
          categoryName: 'Messages_Status',
          translationKey: 'FILTER_MESSAGES_STATUS',
          categoryRelativePath: 'filterMessagesStatus',
          controlDataType: 'DefaultConfig',
          values: [],
          mappingValues: [],
          isFixedFilter: true,
          filterOrder: '3',
          isForcedHide: true,
          isSubCategory: true,
        };
        this.searchConfigService.contactConfiguredSearchIndexConfig.push(
          filterMessagesStatus,
        );
      }
      filterMessagesStatus.values = [
        this.translate.instant('DELIVERED'),
        this.translate.instant('READ'),
      ];
      let filterMessagesAttachment =
        this.searchConfigService.contactConfiguredSearchIndexConfig.find(
          (config) => config.categoryRelativePath == 'filterMessagesAttachment',
        );
      if (!filterMessagesAttachment) {
        filterMessagesAttachment = {
          categoryName: 'Messages_Attachment',
          translationKey: 'FILTER_MESSAGES_CONTENTS',
          categoryRelativePath: 'filterMessagesAttachment',
          controlDataType: 'DefaultConfig',
          values: [],
          mappingValues: [],
          isFixedFilter: true,
          filterOrder: '4',
          isForcedHide: true,
          isSubCategory: true,
        };
        this.searchConfigService.contactConfiguredSearchIndexConfig.push(
          filterMessagesAttachment,
        );
      }
      filterMessagesAttachment.values = [
        this.translate.instant('NO'),
        this.translate.instant('YES'),
      ];
      let filterMessagesLinkAccessed =
        this.searchConfigService.contactConfiguredSearchIndexConfig.find(
          (config) =>
            config.categoryRelativePath == 'filterMessagesLinkAccessed',
        );
      if (!filterMessagesLinkAccessed) {
        filterMessagesLinkAccessed = {
          categoryName: 'Messages_Link_Accessed',
          translationKey: 'FILTER_MESSAGES_CONTENTS_ACCESSED',
          categoryRelativePath: 'filterMessagesLinkAccessed',
          controlDataType: 'DefaultConfig',
          values: [],
          mappingValues: [],
          isFixedFilter: true,
          filterOrder: '5',
          isForcedHide: true,
          isSubCategory: true,
        };
        this.searchConfigService.contactConfiguredSearchIndexConfig.push(
          filterMessagesLinkAccessed,
        );
      }
      filterMessagesLinkAccessed.values = [
        this.translate.instant('NO'),
        this.translate.instant('YES'),
      ];
    }

    const hasSampleActivities = activities
      .filter(
        (activity) =>
          activity instanceof SampleActivity &&
          activity.type == ActivityType.Sample &&
          (activity.state == 1 || activity.statusString == 'Completed'),
      )
      .flatMap((activity) => (activity as SampleActivity).contactID)
      .some((contactId) => !!contactId);
    const allocationOrderText = this.translate.instant('ALLOCATION_ORDER');
    if (
      hasSampleActivities &&
      !myActivitiesByContact.values.includes(allocationOrderText)
    ) {
      myActivitiesByContact.values.push(allocationOrderText);
    }

    const hasCaseIntakeActivities = activities
      .filter(
        (activity) =>
          activity instanceof CaseActivity &&
          activity.type == ActivityType.CaseIntake,
      )
      .flatMap((activity) => (activity as CaseActivity)?.raw?.customerid_value)
      .some((customerId) => !!customerId);
    const customerInquiriesText = this.translate.instant('CUSTOMER_INQUIRIES', {
      globalCustomerText: this.utilityService.globalCustomerText,
    });
    if (
      hasCaseIntakeActivities &&
      !myActivitiesByContact.values.includes(customerInquiriesText)
    ) {
      myActivitiesByContact.values.push(customerInquiriesText);
    }

    const hasEventActivities = _(this.eventsToolService.eventsToolData).flatMap('participants').some();
    const eventsText = this.translate.instant('EVENTS');
    if (
      hasEventActivities &&
      !myActivitiesByContact.values.includes(eventsText)
    ) {
      myActivitiesByContact.values.push(eventsText);
    }
  }

  private mapAllocationsToContactsSearchConfig(contacts: Contact[]) {
    if (this.searchConfigService.isAllocationsMappedToContacts) return;
    const sampleAllocations =
      this.sampleService.contactCustomerSampleAllocations;
    const allocationsConfig =
      this.searchConfigService.contactConfiguredSearchIndexConfig.find(
        (config) => config.categoryName == 'Allocations',
      );
    if (!allocationsConfig) {
      return;
    }
    allocationsConfig.values = _(sampleAllocations)
      .flatMap((csa) => csa.currentCustomerSampleAllocations)
      .map((alloc) => alloc.sampleSKUName)
      .uniq()
      .orderBy((sku) => sku)
      .value();

    for (const csa of sampleAllocations) {
      const thisContact = contacts.find((cont) => cont.ID == csa.contactId);
      if (!thisContact) continue;
      const thisContactAllocations = csa.currentCustomerSampleAllocations.map(
        (alloc) => ({
          contactId: alloc.contactId,
          contactName: alloc.contactName,
          sampleSKUId: alloc.sampleSKUId,
          sampleSKUName: alloc.sampleSKUName,
        }),
      );
      thisContact.contactAllocations.push(...thisContactAllocations);
    }
    this.searchConfigService.isAllocationsMappedToContacts = true;
  }

  private mapConsentsToContactsSearchConfig(contacts: Contact[]) {
    const isMappedConsentsToContactSearch: boolean = contacts?.some(
      (c) => c.hasActiveConsent == this.translate.instant('YES'),
    );
    if (
      this.searchConfigService.isConsentsMappedToContacts &&
      isMappedConsentsToContactSearch
    ) {
      return;
    }

    const activeConsents = this.consentService.allActiveConsentSubject.value;
    if (activeConsents.length === 0) return;

    const consentProductsConfig = this.searchConfigService.contactConfiguredSearchIndexConfig.find(
      (config) => config.categoryName == 'Consent Products',
    );
    const consentTermsConfig = this.searchConfigService.contactConfiguredSearchIndexConfig.find(
      (config) => config.categoryName == 'Consent Terms',
    );

    for (const consent of activeConsents) {
      let thisContact = contacts.find(
        (cont) => cont.ID == consent.indskr_contactid,
      );
      if (!thisContact) continue;
      if (consent.activeConsents.length > 0)
        thisContact.hasActiveConsent = this.translate.instant('YES');
      for (const consentTerm of consent.activeConsents) {
        if (consentTerm.createdon) {
          if (
            !thisContact.lastConsentedDate ||
            consentTerm.createdon > thisContact.lastConsentedDate
          ) {
            thisContact.lastConsentedDate = consentTerm.createdon;
          }
          if (
            !thisContact.consentExpiryDate ||
            consentTerm.indskr_untildate < thisContact.consentExpiryDate
          ) {
            thisContact.consentExpiryDate = consentTerm.indskr_untildate;
          }
        }
        if (consentTerm.products) {
          for (const consentProduct of consentTerm.products) {
            if (
              consentProduct.indskr_productname &&
              !consentProductsConfig.values.some((o) => o == consentProduct.indskr_productname)
            ) {
              consentProductsConfig.values.push(consentProduct.indskr_productname.toString());
            }
          }
        }
        if (
          consentTerm.indskr_consentTermName &&
          !consentTermsConfig.values.some((o) => o == consentTerm.indskr_consentTermName)
        ) {
          consentTermsConfig.values.push(consentTerm.indskr_consentTermName.toString());
        }
      }
    }
    // this.consentService.allConsentChannelSubject.value.forEach(consentChannel=>{
    //     if(consentChannel.indskr_consentType && !this.searchConfigService.contactConfiguredSearchIndexConfig.find(config=> config.categoryName == 'Consent Channels').values.some(o=>o == consentChannel.indskr_consentType)){
    //       this.searchConfigService.contactConfiguredSearchIndexConfig.find(config=> config.categoryName == 'Consent Channels').values.push(consentChannel.indskr_consentType.toString())
    //     }
    // })
    const activeConsentsConfig = this.searchConfigService.contactConfiguredSearchIndexConfig.find(
      (config) => config.categoryRelativePath == 'hasActiveConsent',
    );
    if (activeConsentsConfig) {
      activeConsentsConfig.values = [
        this.translate.instant('YES'),
        this.translate.instant('NO'),
      ];
    }
    this.searchConfigService.isConsentsMappedToContacts = true;
  }

  private async mapChildUsersWithPositionToContactsSearchConfig(
    contacts: Contact[],
  ) {
    if (this.device.isOffline) return;

    this.searchConfigDataService.childUsersWithPositions ??= [];

    if (this.searchConfigDataService.childUsersWithPositions.length === 0) {
      this.searchConfigDataService.childUsersWithPositions =
        await this.contactDataService.getAllChildUsersBasedOnPositionId(
          this.authenticationOfflineService.user.positionID
        );
    }

    const teamusersConfig =
      this.searchConfigService.contactConfiguredSearchIndexConfig.find(
        (config) => config.categoryName == 'Teamusers',
      );
    for (const childUser of this.searchConfigDataService
      .childUsersWithPositions) {
      if (
        childUser.userfullname &&
        !teamusersConfig.mappingValues.some(
          (o) => o.formattedValue == childUser.userfullname,
        )
      ) {
        teamusersConfig.mappingValues.push({
          actualValue: childUser.childpositionid.toString(),
          formattedValue: childUser.userfullname.toString(),
        });
      }
    }

    this.searchConfigDataService.childUsersWithPositions =
      this.searchConfigDataService.childUsersWithPositions.map((childUser) => {
        childUser = pick(childUser, ['childpositionid', 'userfullname']);
        childUser.id = `filter_menu_Teamusers_${childUser['userfullname']}`;
        childUser.label = `${childUser['userfullname']}`;
        childUser.value = `${childUser['childpositionid']}`;
        childUser.isChecked = false;
        childUser.category = 'Teamusers';
        childUser.isExpanded = false;
        childUser.wasCalled = false;
        childUser.childUsers = [];
        childUser.depth = 0;
        return childUser;
      });
  }

  private mapSAPActivitiesToContactsSearchConfig(contacts: Contact[]) {
    if (this.searchConfigService.isSAPActivitiesMappedToContacts) return;
    const sapActivities = this.sapService.sapActivities;
    const sapActivitiesConfig = this.searchConfigService.contactConfiguredSearchIndexConfig.find((config) => config.categoryName == 'Scientific Plans');
    if (!sapActivitiesConfig) {
      return;
    }
    sapActivitiesConfig.values = _(sapActivities)
      .map((sap) => sap.scientificActivityPlanName)
      .uniq()
      .orderBy((name) => name)
      .value();
    this.searchConfigService.isSAPActivitiesMappedToContacts = true;
  }

  public initContactSearchConfig() {
    const contacts = this.getContacts();
    this.searchConfigService.setCustomCategorySearchIndexConfig(
      CustomCategorySearchToolName.CONTACTS,
      this.contactService.contacts,
    );

    if (!this.searchConfigService.isContactProfileDataMappedToContacts) {
      this.contactService.mapContactProfileOfflineDataToContacts();
      this.searchConfigService.isContactProfileDataMappedToContacts = true;
    }
    this.mapAllocationsToContactsSearchConfig(contacts);
    this.mapConsentsToContactsSearchConfig(contacts);
    this.mapSAPActivitiesToContactsSearchConfig(contacts);
    this.mapActivitiesToContactsSearchConfig();
    this.mapChildUsersWithPositionToContactsSearchConfig(contacts);

    if (this.searchConfigService.configUpdateRequired) {
      this.searchConfigService.updateSearchConfigsForSelectedLanguage();
      this.searchConfigService.configUpdateRequired = false;
    } else {
      this.searchConfigService.configUpdateRequired = true;
    }
    let filters = this.searchConfigService.getDefaultFilters('Contacts');
    return filters;
  }

  public async getContactsWithFilters(
    filters: FilterConfig[],
    dateFilter: DateFilterConfig,
  ) {
    filters ??= [];
    const positionsFilter = filters.find(
      (item) => item.categoryName === 'Positions',
    );
    if (positionsFilter) {
      await this.contactService.fetchContactsForConfiguredDisplay(
        false,
        false,
        null,
        false,
        false,
        positionsFilter.selectedFacet,
      );
    }

    const adjustAssessedFilter = (item: FilterConfig) => {
      const facetUpperCase = item.selectedFacet.toUpperCase();
      if (facetUpperCase === 'ASSESSED' || facetUpperCase === 'UNASSESSED') {
        return {
          selectedFacet: this.translate.instant(facetUpperCase),
          categoryPath: item.selectedFacet,
          categoryName: item.selectedFacet,
          categoryDisplayName: item.selectedFacet,
        };
      }
      return item;
    };
    // filters = filters.map(adjustAssessedFilter);
    this.initializeSubFilterSelections(filters);
    //second level search for contacts
    filters = this.mapForUserTag(filters);
    let filteredContacts = this.getContacts();
    let query = _(filteredContacts);
    let predicates: Predicate<Contact>[] = [];
    // Apply entity level character search
    predicates.push(this.getEntityLevelCharSearchPredicate(filters));
    // Apply list tag filter
    predicates.push(this.getListTagPredicate(filters));

    // Populate configured from
    this.populateConfiguredFrom(filters);

    // Group filters by category
    const filtersGroupedByCategory = this.groupFiltersByCategory(filters);

    // Apply filters grouped by category
    predicates.push(
      ...(await this.getGroupedPredicates(
        query,
        filtersGroupedByCategory,
        dateFilter,
      )),
    );

    // Step 6: Tag suggestion filter
    predicates.push(this.getTagSuggestionPredicate(filters));
    predicates = predicates.filter(Boolean);

    if (predicates.length > 0) {
      query = predicates.reduce(
        (acc, predicate) => acc.filter((c) => predicate(c)),
        query,
      );
    }

    // Final Result
    return query;
  }


}
